/**
 * ═══════════════════════════════════════════════════════════════════════
 * EventBus — Lightweight In-Process Pub/Sub (Hardened)
 * ═══════════════════════════════════════════════════════════════════════
 *
 * SECURITY:
 *   • Event type validation — only known event types are emitted
 *   • Source field validation — must be 'remote' | 'local' | 'cross-tab'
 *   • Event shape validation — type and source are required
 *   • Max listener cap per event type to prevent memory exhaustion
 *   • Singleton is frozen to prevent prototype tampering
 *
 * Event shape:
 *   {
 *     type:      'VISIT_CREATED' | 'VISIT_UPDATED' | 'QUEUE_MOVED' | …
 *     payload:   { … },          // minimal delta — not full entity
 *     tenantId:  'org_abc123',
 *     timestamp: 1708500000000,
 *     source:    'remote' | 'local' | 'cross-tab',
 *     causedBy:  'user_xyz'       // optional — who triggered it
 *   }
 */

// ── Security: Only these event types can flow through the bus ─────────
const ALLOWED_EVENT_TYPES = new Set([
  // Visit lifecycle
  'VISIT_CREATED',
  'VISIT_UPDATED',
  'VISIT_DELETED',
  'VISIT_STATUS_CHANGED',
  'VISIT_METADATA_ADDED',
  // Queue operations
  'QUEUE_MOVED',
  'QUEUE_STARTED',
  'QUEUE_SKIPPED',
  // Skip / Requeue
  'VISIT_SKIPPED',
  'VISIT_REQUEUED',
  // Staff / Doctor
  'DOCTOR_AVAILABILITY_CHANGED',
  'DOCTOR_STATUS_CHANGED',
  'STAFF_UPDATED',
  // Billing
  'BILLING_STATUS_CHANGED',
  'VISIT_BILLING_UPDATED',
  // Appointments + calendar blocks (Confirm Appointments)
  'APPOINTMENT_CREATED',
  'APPOINTMENT_UPDATED',
  'APPOINTMENT_CANCELLED',
  'APPOINTMENT_CHECKED_IN',
  'CALENDAR_BLOCK_CHANGED',
  'APPT_IMPACT_CHANGED',
  // Treatment Plans + Sittings (Phase 1)
  'PLAN_CREATED',
  'PLAN_STATUS_CHANGED',
  'SITTING_SCHEDULED',
  'SITTING_AWAITING_PAYMENT',
  'SITTING_PAYMENT_CLEARED',
  // Phase 3 — strict-plan one-by-one promotion
  'SITTING_HELD',
  'SITTING_READY',
  'SITTING_EXECUTION_STARTED',
  'SITTING_COMPLETED',
  'SITTING_PENDING_APPROVAL',
  'SITTING_APPROVED',
  'SITTING_REJECTED',
  'SITTING_REASSIGNED',
  'SITTING_VISIT_MOVED',
  'SITTING_CANCELLED',
  'SITTING_WALKIN_NOTIFICATION',
  // Phase 4.0i — note attached to a sitting (separate from visit metadata).
  'SITTING_NOTE_ADDED',
  // Local-only refresh signal — fires on chart open + after chart
  // actions to nudge every store (dentalHistoryStore, sittingStore)
  // to re-confirm freshness without waiting for the WS round-trip.
  'TOOTH_CHART_REFRESH',
  // Phase 1b — handoff + Rx approval
  'VISIT_HANDLER_CHANGED',
  'PRESCRIPTION_APPROVED',
  'PRESCRIPTION_REJECTED',
  // Admission / IPD — C2 live occupancy board bed-state change
  'BED_STATE_CHANGED',
  // Bulk refresh (polling detected many changes)
  'BULK_REFRESH',
  // Connection lifecycle (must flow for reconnect/reconcile)
  'REALTIME_CONNECTED',
  'REALTIME_DISCONNECTED',
  'REALTIME_RECONNECTING',
  // Internal: cross-tab reconciliation
  '__STATE_SNAPSHOT__',
  // ABDM M1 — Scan & Share patient profile received via gateway callback
  'abha_scan_share',
  'abha_scan_share_claimed',
]);

const VALID_SOURCES = new Set(['remote', 'local', 'cross-tab']);
const MAX_LISTENERS_PER_TYPE = 20;

class EventBus {
  constructor() {
    /** @type {Map<string, Set<Function>>} */
    this._listeners = new Map();

    /** @type {Set<Function>} wildcard / catch-all */
    this._wildcardListeners = new Set();

    /** @type {Array<{eventId: string, timestamp: number}>} */
    this._recentEvents = [];

    this._maxRecent = 50; // ring buffer size for dedup
  }

  // ── Subscribe to a specific event type ──────────────────────────────
  on(type, callback) {
    if (typeof type !== 'string' || typeof callback !== 'function') return () => {};

    if (!this._listeners.has(type)) {
      this._listeners.set(type, new Set());
    }

    const set = this._listeners.get(type);

    // Security: cap listeners to prevent memory exhaustion
    if (set.size >= MAX_LISTENERS_PER_TYPE) {
      console.warn(`[EventBus] Max listeners (${MAX_LISTENERS_PER_TYPE}) reached for "${type}"`);
      return () => {};
    }

    set.add(callback);

    // Return unsubscribe function
    return () => {
      const s = this._listeners.get(type);
      if (s) {
        s.delete(callback);
        if (s.size === 0) this._listeners.delete(type);
      }
    };
  }

  // ── Subscribe to ALL events (for logging / cross-tab relay) ─────────
  onAny(callback) {
    if (typeof callback !== 'function') return () => {};
    this._wildcardListeners.add(callback);
    return () => this._wildcardListeners.delete(callback);
  }

  // ── Emit an event ───────────────────────────────────────────────────
  emit(event) {
    // Security: validate event shape
    if (!event || typeof event !== 'object') return;
    if (typeof event.type !== 'string') return;

    // Security: only allow known event types
    if (!ALLOWED_EVENT_TYPES.has(event.type)) {
      console.warn(`[EventBus] Blocked unknown event type: "${event.type}"`);
      return;
    }

    // Security: validate source field
    if (event.source && !VALID_SOURCES.has(event.source)) {
      console.warn(`[EventBus] Blocked invalid source: "${event.source}"`);
      return;
    }

    // Dedup: skip if we already processed this exact event recently
    if (this._isDuplicate(event)) return;

    // Track for dedup
    this._trackEvent(event);

    // Notify type-specific listeners
    const listeners = this._listeners.get(event.type);
    if (listeners) {
      listeners.forEach((cb) => {
        try {
          cb(event);
        } catch (err) {
          console.error(`[EventBus] Listener error for ${event.type}:`, err);
        }
      });
    }

    // Notify wildcard listeners
    this._wildcardListeners.forEach((cb) => {
      try {
        cb(event);
      } catch (err) {
        console.error('[EventBus] Wildcard listener error:', err);
      }
    });
  }

  // ── Batch emit (for polling responses with multiple changes) ────────
  emitBatch(events) {
    if (!Array.isArray(events)) return;
    events.forEach((e) => this.emit(e));
  }

  // ── Deduplication ───────────────────────────────────────────────────
  _isDuplicate(event) {
    if (!event.eventId) return false; // no ID → can't dedup
    return this._recentEvents.some((e) => e.eventId === event.eventId);
  }

  _trackEvent(event) {
    if (event.eventId) {
      this._recentEvents.push({
        eventId: event.eventId,
        timestamp: event.timestamp || Date.now(),
      });
      // Trim ring buffer — use splice() for in-place mutation because
      // Object.freeze(eventBus) makes property reassignment throw in strict mode
      if (this._recentEvents.length > this._maxRecent) {
        this._recentEvents.splice(0, this._recentEvents.length - this._maxRecent);
      }
    }
  }

  // ── Cleanup ─────────────────────────────────────────────────────────
  removeAllListeners() {
    this._listeners.clear();
    this._wildcardListeners.clear();
  }

  // ── Debug ───────────────────────────────────────────────────────────
  listenerCount(type) {
    if (type) return this._listeners.get(type)?.size || 0;
    let total = this._wildcardListeners.size;
    this._listeners.forEach((set) => (total += set.size));
    return total;
  }
}

// Singleton — one bus per browser tab, frozen to prevent tampering
const eventBus = new EventBus();
Object.freeze(eventBus);
export default eventBus;
